import { useState, useEffect } from 'react'
import { Copy, ExternalLink, Info, Check, Save, ChevronDown } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface NilGoField {
  id: string
  category: string
  label: string
  value: string
  required: boolean
}

// Maps form field ids to the nilGoFields JSONB keys stored in the DB
export const FIELD_TO_NIL_GO_KEY: Record<string, string> = {
  brand_name: 'brandLegalName',
  total_compensation: 'totalCompensation',
  nature_of_relationship: 'natureOfRelationship',
  platforms: 'primaryPlatform',
  platforms_secondary: 'secondaryPlatform',
  deal_executed_at: 'agreementStartDate',
  end_date: 'agreementEndDate',
  service_description: 'serviceDescription',
  content_deadline: 'contentDeadline',
  usage_rights: 'usageRights',
  exclusivity: 'exclusivity',
  termination: 'terminationClause',
  payment_structure: 'paymentStructure',
  brand_contact_name: 'brandContactName',
  brand_contact_email: 'brandContactEmail',
}

export interface NilGoSubmissionFormProps {
  /** Persisted field values from the DB / contract scan */
  nilGoFields?: Record<string, string | null> | null
  /** Fallback brand name if not in nilGoFields */
  brandName?: string | null
  /** Fallback payout/compensation if not in nilGoFields */
  paymentAmount?: string | number | null
  /** Platform(s) for the deal */
  platform?: string | string[] | null
  /** Deal execution / agreement start date (ISO string) */
  dealExecutedAt?: string | null
  /** Agreement end date (ISO string) */
  endDate?: string | null
  /** Content due date (ISO string) */
  contentDueDate?: string | null
  /** Performance instructions / deliverables description */
  instructions?: string | null
  /** Called with the updated nilGoFields map when the user saves */
  onSave: (nilGoFields: Record<string, string>) => Promise<void>
  /** Label for the save button. Defaults to "Save Fields" */
  saveLabel?: string
  /** Whether to show the "Open NIL Go" action button. Defaults to false */
  showOpenNilGo?: boolean
  /** Override the info callout text */
  infoText?: string
  /** 'dark' for the athlete app (default); 'light' for the light apps: black text, plain white */
  tone?: 'dark' | 'light'
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatPayout(payout: string | number): string {
  const amount = typeof payout === 'number' ? payout : parseFloat(payout)
  if (isNaN(amount)) return String(payout)
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function buildFields(props: NilGoSubmissionFormProps): NilGoField[] {
  const fields = props.nilGoFields ?? {}
  const brandName = props.brandName ?? ''
  const platform = props.platform ?? ''
  const dealExecutedAt = props.dealExecutedAt ?? ''
  const endDate = props.endDate ?? ''

  const startDate = fields.agreementStartDate ?? dealExecutedAt
  const contractEndDate = fields.agreementEndDate ?? endDate
  const resolvedBrandName = fields.brandLegalName ?? (brandName ? `${brandName} Inc.` : '')
  const resolvedCompensation = fields.totalCompensation
    ? formatPayout(fields.totalCompensation)
    : formatPayout(props.paymentAmount ?? '0')

  return [
    {
      id: 'brand_name',
      category: 'Basic Details',
      label: 'Brand/Company Name',
      value: resolvedBrandName,
      required: true,
    },
    {
      id: 'total_compensation',
      category: 'Basic Details',
      label: 'Compensation Amount',
      value: resolvedCompensation,
      required: true,
    },
    {
      id: 'nature_of_relationship',
      category: 'Basic Details',
      label: 'Deal Type',
      value: fields.natureOfRelationship ?? 'Social Media Endorsement',
      required: true,
    },
    {
      id: 'platforms',
      category: 'Platform Data',
      label: 'Primary Platform',
      value: fields.primaryPlatform ?? (Array.isArray(platform) ? (platform[0] ?? '') : platform),
      required: true,
    },
    {
      id: 'platforms_secondary',
      category: 'Platform Data',
      label: 'Secondary Platform',
      value:
        fields.secondaryPlatform ??
        (Array.isArray(platform) && platform.length > 1 ? platform[1] : ''),
      required: false,
    },
    {
      id: 'deal_executed_at',
      category: 'Dates',
      label: 'Agreement Start Date',
      value: startDate ? formatDate(startDate) : '',
      required: true,
    },
    {
      id: 'end_date',
      category: 'Dates',
      label: 'Agreement End Date',
      value: contractEndDate ? formatDate(contractEndDate) : '',
      required: true,
    },
    {
      id: 'service_description',
      category: 'Performance',
      label: 'Performance Deliverable',
      value: fields.serviceDescription ?? props.instructions ?? '',
      required: true,
    },
    {
      id: 'content_deadline',
      category: 'Performance',
      label: 'Content Deadline',
      value:
        fields.contentDeadline ?? (props.contentDueDate ? formatDate(props.contentDueDate) : ''),
      required: true,
    },
    {
      id: 'usage_rights',
      category: 'Rights & Restrictions',
      label: 'Usage Rights',
      value: fields.usageRights ?? '',
      required: true,
    },
    {
      id: 'exclusivity',
      category: 'Rights & Restrictions',
      label: 'Exclusivity Clause',
      value: fields.exclusivity ?? '',
      required: false,
    },
    {
      id: 'termination',
      category: 'Contract Terms',
      label: 'Termination Clause',
      value: fields.terminationClause ?? '',
      required: false,
    },
    {
      id: 'payment_structure',
      category: 'Contract Terms',
      label: 'Payment Schedule',
      value: fields.paymentStructure ?? '',
      required: true,
    },
    {
      id: 'brand_contact_name',
      category: 'Contact Info',
      label: 'Brand Contact Name',
      value: fields.brandContactName ?? '',
      required: true,
    },
    {
      id: 'brand_contact_email',
      category: 'Contact Info',
      label: 'Brand Contact Email',
      value: fields.brandContactEmail ?? '',
      required: true,
    },
  ]
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type Tone = 'dark' | 'light'
const STYLE = {
  dark: {
    wrap: 'bg-card border border-foreground/8',
    title: 'text-foreground',
    sub: 'text-gray-500',
    info: 'bg-[#9CF0FB]/5 border border-[#9CF0FB]/15',
    infoIcon: 'text-[#9CF0FB]',
    infoText: 'text-gray-600',
    section: 'bg-card border border-foreground/8',
    sectionHover: 'hover:bg-foreground/5',
    category: 'text-[#9CF0FB]',
    count: 'text-gray-500',
    chevron: 'text-gray-500',
    row: 'border-foreground/5',
    label: 'text-gray-600',
    copy: 'text-gray-400 hover:text-gray-600',
    input:
      'bg-card border border-foreground/8 hover:border-foreground/15 focus:border-foreground/30 text-foreground placeholder:text-gray-400',
    button: 'bg-card border border-foreground/15 hover:border-foreground/30 text-foreground',
    done: 'text-[#4d7c0f]',
  },
  light: {
    wrap: 'bg-[#FFFFFF] border border-[#E5E7EB]',
    title: 'text-[#111827]',
    sub: 'text-[#6B7280]',
    info: 'bg-[#F9FAFB] border border-[#E5E7EB]',
    infoIcon: 'text-[#111827]',
    infoText: 'text-[#374151]',
    section: 'bg-[#FFFFFF] border border-[#E5E7EB]',
    sectionHover: 'hover:bg-[#F9FAFB]',
    category: 'text-[#111827]',
    count: 'text-[#6B7280]',
    chevron: 'text-[#6B7280]',
    row: 'border-[#F3F4F6]',
    label: 'text-[#374151]',
    copy: 'text-[#6B7280] hover:text-[#111827]',
    input:
      'bg-[#FFFFFF] border border-[#D1D5DB] hover:border-[#9CA3AF] focus:border-[#111827] text-[#111827] placeholder:text-[#9CA3AF]',
    button: 'bg-[#FFFFFF] border border-[#D1D5DB] hover:border-[#111827] text-[#111827]',
    done: 'text-[#111827]',
  },
} satisfies Record<Tone, Record<string, string>>

function FieldRow({
  field,
  onChange,
  tone,
}: {
  field: NilGoField
  onChange: (id: string, value: string) => void
  tone: Tone
}) {
  const t = STYLE[tone]
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(field.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`py-3 border-b ${t.row} last:border-0`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className={`text-xs ${t.label}`}>
          {field.label}
          {field.required && (
            <span className={`${tone === 'light' ? 'text-[#6B7280]' : 'text-[#4d7c0f]'} ml-1`}>
              *
            </span>
          )}
        </p>
        <button
          onClick={handleCopy}
          className={`shrink-0 p-1 ${t.copy} transition-colors`}
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className={`w-3.5 h-3.5 ${t.done}`} />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      <textarea
        value={field.value}
        onChange={(e) => onChange(field.id, e.target.value)}
        placeholder="Not set"
        rows={1}
        className={`w-full ${t.input} rounded-lg px-3 py-2 text-base outline-hidden transition-colors resize-none overflow-hidden`}
        style={{ fieldSizing: 'content' } as React.CSSProperties}
      />
    </div>
  )
}

function CategorySection({
  category,
  fields,
  onChange,
  defaultOpen = false,
  tone,
}: {
  category: string
  fields: NilGoField[]
  onChange: (id: string, value: string) => void
  defaultOpen?: boolean
  tone: Tone
}) {
  const t = STYLE[tone]
  const [open, setOpen] = useState(defaultOpen)
  const filledCount = fields.filter((f) => f.value.trim().length > 0).length

  return (
    <div className={`${t.section} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 ${t.sectionHover} transition-colors`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`text-sm font-semibold ${t.category}`}>{category}</span>
          <span className={`text-[11px] ${t.count}`}>
            {filledCount}/{fields.length} filled
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 ${t.chevron} transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-1">
          {fields.map((field) => (
            <FieldRow key={field.id} field={field} onChange={onChange} tone={tone} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export const NilGoSubmissionForm = ({
  saveLabel = 'Save Fields',
  showOpenNilGo = false,
  infoText,
  onSave,
  tone = 'dark',
  ...rest
}: NilGoSubmissionFormProps) => {
  const t = STYLE[tone]
  const [fieldList, setFieldList] = useState<NilGoField[]>(() => buildFields({ ...rest, onSave }))
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [copiedAll, setCopiedAll] = useState(false)

  // Re-populate when nilGoFields changes (e.g. after a contract scan)
  useEffect(() => {
    setFieldList(buildFields({ ...rest, onSave }))
  }, [rest.nilGoFields]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (id: string, value: string) => {
    setSaveSuccess(false)
    setFieldList((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)))
  }

  const handleCopyAll = async () => {
    const lines = fieldList
      .filter((f) => f.value.trim() !== '')
      .map((f) => `${f.label}: ${f.value}`)
      .join('\n')
    await navigator.clipboard.writeText(lines)
    setCopiedAll(true)
    setTimeout(() => setCopiedAll(false), 2500)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveSuccess(false)
    try {
      const nilGoFields: Record<string, string> = {}
      for (const field of fieldList) {
        const key = FIELD_TO_NIL_GO_KEY[field.id]
        if (key) nilGoFields[key] = field.value
      }
      await onSave(nilGoFields)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  // Group fields by category
  const groups: { category: string; items: NilGoField[] }[] = []
  fieldList.forEach((f) => {
    const existing = groups.find((g) => g.category === f.category)
    if (existing) existing.items.push(f)
    else groups.push({ category: f.category, items: [f] })
  })

  const defaultInfoText =
    'The agent extracted these fields from the deal contract and your profile. Review each field — you can edit any value by clicking it. When ready, open NIL Go and paste each section.'

  return (
    <div className={`${t.wrap} rounded-2xl p-5 space-y-4`}>
      {/* Header */}
      <div>
        <p className={`${t.title} font-semibold text-base`}>NIL Go Submission Details</p>
        <p className={`${t.sub} text-sm mt-0.5`}>
          Review and copy these fields to submit your collaboration to NIL Go
        </p>
      </div>

      {/* Info callout */}
      <div className={`flex items-start gap-2.5 ${t.info} rounded-xl px-4 py-3`}>
        <Info className={`w-4 h-4 ${t.infoIcon} shrink-0 mt-0.5`} />
        <p className={`${t.infoText} text-xs leading-relaxed`}>{infoText ?? defaultInfoText}</p>
      </div>

      {/* Fields grouped by category */}
      <div className="space-y-2">
        {groups.map((g, i) => (
          <CategorySection
            key={g.category}
            category={g.category}
            fields={g.items}
            onChange={handleFieldChange}
            defaultOpen={i === 0}
            tone={tone}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-2.5">
        <button
          onClick={handleCopyAll}
          className={`w-full flex items-center justify-center gap-2 ${t.button} text-sm font-semibold rounded-xl py-3 transition-all`}
        >
          {copiedAll ? (
            <>
              <Check className={`w-4 h-4 ${t.done}`} />
              <span className={t.done}>Copied to Clipboard</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy All Fields
            </>
          )}
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`w-full flex items-center justify-center gap-2 ${t.button} text-sm font-semibold rounded-xl py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {saveSuccess ? (
            <>
              <Check className={`w-4 h-4 ${t.done}`} />
              <span className={t.done}>Saved</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving…' : saveLabel}
            </>
          )}
        </button>

        {showOpenNilGo && (
          <a
            href="https://nilgo.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#C0FD72] hover:bg-[#d2ff9c] text-black text-sm font-semibold rounded-xl py-3 transition-colors"
          >
            Open NIL Go
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  )
}
